import React, { useEffect, useRef, useState, useContext } from "react";

import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";

import useSettings from "../../hooks/useSettings";

import { makeStyles } from "@material-ui/core/styles";
import { grey, blue } from "@material-ui/core/colors";

import { Tab, Tabs, TextField, Typography } from "@material-ui/core";
import { HelpCircle } from "lucide-react";
import { i18n } from "../../translate/i18n";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import CustomToggleSwitch from "../CustomToggleSwitch";
import AppTooltip from "../AppTooltip";
import MessageVariablesPicker from "../MessageVariablesPicker";
import { useCurrencyContext } from "../../context/Currency/CurrencyContext";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  return {
    container: {
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
    },
    fixedHeightPaper: {
      padding: theme.spacing(2),
      display: "flex",
      overflow: "auto",
      flexDirection: "column",
      height: 240,
    },
    cardAvatar: {
      fontSize: "55px",
      color: grey[500],
      backgroundColor: "#ffffff",
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    cardTitle: {
      fontSize: "18px",
      color: blue[700],
    },
    cardSubtitle: {
      color: grey[600],
      fontSize: "14px",
    },
    alignRight: {
      textAlign: "right",
    },
    fullWidth: {
      width: "100%",
    },
    selectContainer: {
      width: "100%",
      textAlign: "left",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },
    globalSelect: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },
    summaryTimeSelect: {
      marginTop: 10,
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
      "& .MuiSelect-select": {
        fontSize: "0.9rem",
        fontWeight: 500,
      },
    },
    globalMenuPaper: {
      borderRadius: "14px !important",
      boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
      border: "none",
      background: isDark ? "#121212" : "#ffffff",
      backgroundImage: "none",
      marginTop: 4,
      ...(theme.scrollbarStyles || {}),
      "& ul": {
        padding: "6px",
      },
      "& li.MuiMenuItem-root": {
        borderRadius: 8,
        padding: "9px 12px",
        marginBottom: 4,
        fontSize: 14,
        fontWeight: 500,
        color: isDark ? "#ffffff" : "#1a1a1a",
        "&:hover": {
          background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
        },
        "&.Mui-selected": {
          background: "rgba(6, 207, 156, 0.10)",
        },
        "&.Mui-selected:hover": {
          background: "rgba(6, 207, 156, 0.15)",
        },
        "&:last-child": {
          marginBottom: 0,
        },
      },
    },
    tab: {
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f2f2f2",
      borderRadius: 4,
      width: "100%",
      "& .MuiTabs-flexContainer": {
        justifyContent: "center",
      },
    },
    toggleItem: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      marginBottom: 4,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.05)"
          : "#f5f5f5",
        borderColor: isDark
          ? "rgba(255,255,255,0.20)"
          : "#bdbdbd",
      },
    },
    toggleCard: {
      display: "flex",
      flexDirection: "column",
      padding: "10px 16px",
      borderRadius: 10,
      border: isDark
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(0,0,0,0.10)",
      background: isDark ? "rgba(255,255,255,0.02)" : "transparent",
      marginBottom: 4,
      transition: "all 0.2s ease",
      "&:hover": {
        backgroundColor: isDark
          ? "rgba(255,255,255,0.05)"
          : "#f5f5f5",
        borderColor: isDark
          ? "rgba(255,255,255,0.20)"
          : "#bdbdbd",
      },
    },
    toggleCardRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      gap: 12,
    },
    toggleExpanded: {
      marginTop: 10,
      paddingTop: 10,
      borderTop: isDark
        ? "1px solid rgba(255,255,255,0.08)"
        : "1px solid rgba(0,0,0,0.08)",
      width: "100%",
      "& .MuiOutlinedInput-root": {
        borderRadius: 12,
      },
    },
    variablesPickerWrap: {
      marginTop: 8,
    },
    toggleLabel: {
      fontSize: "0.9rem",
      color: isDark ? "#e9edef" : "#424242",
      fontWeight: 500,
    },
    toggleLabelWithHelp: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
    },
    fieldLabel: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      color: isDark ? "#e9edef" : "#111b21",
      lineHeight: 1.2,
      marginBottom: 6,
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      verticalAlign: "middle",
      color: isDark ? "#8696a0" : "#667781",
      cursor: "help",
      opacity: 0.6,
      transition: "opacity 0.15s ease, color 0.15s ease",
      "&:hover": {
        opacity: 1,
        color: theme.palette.primary.main,
      },
    },
    helpTooltipTitle: {
      fontWeight: 600,
      marginBottom: 6,
      fontSize: 12,
    },
    helpTooltipSteps: {
      margin: 0,
      paddingLeft: 16,
      "& li": {
        marginBottom: 3,
      },
      "& li:last-child": {
        marginBottom: 0,
      },
    },
    toggleHelper: {
      fontSize: "0.75rem",
      color: isDark ? "rgba(255,255,255,0.5)" : "#999",
      marginTop: 2,
    },
    optionsContainer: {
      flex: 1,
      minHeight: 0,
      padding: "12px",
      overflowX: "hidden",
      ...(theme.scrollbarStyles || {}),
    },
  };
});

const tOption = (key, fallback) => {
  const fullKey = `settings.settingsConfig.options.${key}`;
  const value = i18n.t(fullKey);
  return !value || value === fullKey ? fallback : value;
};

const WELCOME_MESSAGE_LANGUAGES = [
  { code: "pt", label: "Português" },
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "tr", label: "Türkçe" },
  { code: "ar", label: "العربية" },
];

const DEFAULT_WELCOME_MESSAGES = {
  pt: "Olá {name}, bem-vindo(a) à {companyName}! 🎉\n\nSeus dados de cadastro:\n\n👤 Nome: {name}\n🏢 Empresa: {companyName}\n📧 Email: {email}\n📱 Telefone: {phone}\n📅 Trial válido até: {trialDate}",
  en: "Hello {name}, welcome to {companyName}! 🎉\n\nYour account details:\n\n👤 Name: {name}\n🏢 Company: {companyName}\n📧 Email: {email}\n📱 Phone: {phone}\n📅 Trial valid until: {trialDate}",
  es: "Hola {name}, bienvenido(a) a {companyName}! 🎉\n\nDatos de su cuenta:\n\n👤 Nombre: {name}\n🏢 Empresa: {companyName}\n📧 Email: {email}\n📱 Teléfono: {phone}\n📅 Prueba válida hasta: {trialDate}",
  tr: "Merhaba {name}, {companyName}'a hoş geldiniz! 🎉\n\nHesap bilgileriniz:\n\n👤 Ad: {name}\n🏢 Şirket: {companyName}\n📧 E-posta: {email}\n📱 Telefon: {phone}\n📅 Deneme süresi bitiş tarihi: {trialDate}",
  ar: "مرحباً {name}، أهلاً بك في {companyName}! 🎉\n\nبيانات حسابك:\n\n👤 الاسم: {name}\n🏢 الشركة: {companyName}\n📧 البريد الإلكتروني: {email}\n📱 الهاتف: {phone}\n📅 التجربة صالحة حتى: {trialDate}",
};

const parseWelcomeMessages = (value) => {
  if (!value || !String(value).trim()) {
    return { ...DEFAULT_WELCOME_MESSAGES };
  }

  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return {
        ...DEFAULT_WELCOME_MESSAGES,
        ...WELCOME_MESSAGE_LANGUAGES.reduce((acc, lang) => {
          acc[lang.code] = parsed[lang.code] || parsed[lang.code === "pt" ? "pt-BR" : lang.code] || DEFAULT_WELCOME_MESSAGES[lang.code];
          return acc;
        }, {}),
      };
    }
  } catch {}

  return {
    ...DEFAULT_WELCOME_MESSAGES,
    pt: value,
  };
};

const stringifyWelcomeMessages = (messages) =>
  JSON.stringify(
    WELCOME_MESSAGE_LANGUAGES.reduce((acc, lang) => {
      acc[lang.code] = messages?.[lang.code] || "";
      return acc;
    }, {})
  );

export default function Options(props) {
  const { oldSettings, settings, scheduleTypeChanged, user } = props;

  const classes = useStyles();
  const aiSummaryTimeOptions = Array.from({ length: 96 }, (_, index) => {
    const totalMinutes = index * 15;
    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
    const minutes = String(totalMinutes % 60).padStart(2, "0");
    return `${hours}:${minutes}`;
  });
  const { currency, updateCurrency, getAvailableCurrencies, loading: loadingCurrency } = useCurrencyContext();
  const [userRating, setUserRating] = useState("disabled");
  const [scheduleType, setScheduleType] = useState("disabled");
  const [chatBotType, setChatBotType] = useState("text");

  const [loadingUserRating, setLoadingUserRating] = useState(false);
  const [loadingScheduleType, setLoadingScheduleType] = useState(false);

  const [userCreation, setUserCreation] = useState("disabled");
  const [loadingUserCreation, setLoadingUserCreation] = useState(false);

  const [SendGreetingAccepted, setSendGreetingAccepted] = useState("enabled");
  const [loadingSendGreetingAccepted, setLoadingSendGreetingAccepted] = useState(false);

  const [UserRandom, setUserRandom] = useState("enabled");
  const [loadingUserRandom, setLoadingUserRandom] = useState(false);

  const [SettingsTransfTicket, setSettingsTransfTicket] = useState("enabled");
  const [loadingSettingsTransfTicket, setLoadingSettingsTransfTicket] = useState(false);

  const [AcceptCallWhatsapp, setAcceptCallWhatsapp] = useState("enabled");
  const [loadingAcceptCallWhatsapp, setLoadingAcceptCallWhatsapp] = useState(false);

  const [sendSignMessage, setSendSignMessage] = useState("enabled");
  const [loadingSendSignMessage, setLoadingSendSignMessage] = useState(false);

  const [sendGreetingMessageOneQueues, setSendGreetingMessageOneQueues] = useState("enabled");
  const [loadingSendGreetingMessageOneQueues, setLoadingSendGreetingMessageOneQueues] = useState(false);

  const [sendQueuePosition, setSendQueuePosition] = useState("enabled");
  const [loadingSendQueuePosition, setLoadingSendQueuePosition] = useState(false);

  const [sendFarewellWaitingTicket, setSendFarewellWaitingTicket] = useState("enabled");
  const [loadingSendFarewellWaitingTicket, setLoadingSendFarewellWaitingTicket] = useState(false);

  const [acceptAudioMessageContact, setAcceptAudioMessageContact] = useState("enabled");
  const [loadingAcceptAudioMessageContact, setLoadingAcceptAudioMessageContact] = useState(false);

  //LGPD
  const [enableLGPD, setEnableLGPD] = useState("disabled");
  const [loadingEnableLGPD, setLoadingEnableLGPD] = useState(false);

  const [lgpdMessage, setLGPDMessage] = useState("");
  const [loadinglgpdMessage, setLoadingLGPDMessage] = useState(false);

  const [lgpdLink, setLGPDLink] = useState("");
  const [loadingLGPDLink, setLoadingLGPDLink] = useState(false);

  const [lgpdDeleteMessage, setLGPDDeleteMessage] = useState("disabled");
  const [loadingLGPDDeleteMessage, setLoadingLGPDDeleteMessage] = useState(false);

  const [lgpdConsent, setLGPDConsent] = useState("disabled");
  const [loadingLGPDConsent, setLoadingLGPDConsent] = useState(false);

  const [lgpdHideNumber, setLGPDHideNumber] = useState("disabled");
  const [loadingLGPDHideNumber, setLoadingLGPDHideNumber] = useState(false);

  // Tag obrigatoria
  const [requiredTag, setRequiredTag] = useState("enabled")
  const [loadingRequiredTag, setLoadingRequiredTag] = useState(false)

  // Fechar ticket ao transferir para outro setor
  const [closeTicketOnTransfer, setCloseTicketOnTransfer] = useState(false)
  const [loadingCloseTicketOnTransfer, setLoadingCloseTicketOnTransfer] = useState(false)

  // Usar carteira de clientes
  const [directTicketsToWallets, setDirectTicketsToWallets] = useState(false)
  const [loadingDirectTicketsToWallets, setLoadingDirectTicketsToWallets] = useState(false)

  //MENSAGENS CUSTOMIZADAS
  const [transferMessage, setTransferMessage] = useState("Seu Atendimento foi Transferido para o setor ${queue.name},Aguarde atendimento por favor...");
  const [loadingTransferMessage, setLoadingTransferMessage] = useState(false);

  const [greetingAcceptedMessage, setGreetingAcceptedMessage] = useState("");
  const [loadingGreetingAcceptedMessage, setLoadingGreetingAcceptedMessage] = useState(false);

  const [AcceptCallWhatsappMessage, setAcceptCallWhatsappMessage] = useState("");
  const [loadingAcceptCallWhatsappMessage, setLoadingAcceptCallWhatsappMessage] = useState(false);

  const [sendQueuePositionMessage, setSendQueuePositionMessage] = useState("");
  const [loadingSendQueuePositionMessage, setLoadingSendQueuePositionMessage] = useState(false);

  const [showNotificationPending, setShowNotificationPending] = useState(false);
  const [loadingShowNotificationPending, setLoadingShowNotificationPending] = useState(false);

  const [autoTranslate, setAutoTranslate] = useState("disabled");
  const [loadingAutoTranslate, setLoadingAutoTranslate] = useState(false);
  const [translateApiKey, setTranslateApiKey] = useState("");
  const [loadingTranslateApiKey, setLoadingTranslateApiKey] = useState(false);

  const [aiAutoSummary, setAiAutoSummary] = useState("disabled");
  const [loadingAiAutoSummary, setLoadingAiAutoSummary] = useState(false);
  const [aiAutoSummaryTime, setAiAutoSummaryTime] = useState("23:00");
  const [loadingAiAutoSummaryTime, setLoadingAiAutoSummaryTime] = useState(false);
  const [aiSummaryApiKey, setAiSummaryApiKey] = useState("");
  const [loadingAiSummaryApiKey, setLoadingAiSummaryApiKey] = useState(false);
  const aiSummaryTimeSelectOptions =
    aiAutoSummaryTime && !aiSummaryTimeOptions.includes(aiAutoSummaryTime)
      ? [aiAutoSummaryTime, ...aiSummaryTimeOptions]
      : aiSummaryTimeOptions;

  const [sendWelcomeMessage, setSendWelcomeMessage] = useState("disabled");
  const [loadingSendWelcomeMessage, setLoadingSendWelcomeMessage] = useState(false);
  const [welcomeMessageTemplate, setWelcomeMessageTemplate] = useState(DEFAULT_WELCOME_MESSAGES);
  const [loadingWelcomeMessageTemplate, setLoadingWelcomeMessageTemplate] = useState(false);

  // Saída IA do FlowBuilder
  const [flowbuilderAI, setFlowbuilderAI] = useState("disabled");
  const [loadingFlowbuilderAI, setLoadingFlowbuilderAI] = useState(false);
  const [flowbuilderAIApiKey, setFlowbuilderAIApiKey] = useState("");
  const [loadingFlowbuilderAIApiKey, setLoadingFlowbuilderAIApiKey] = useState(false);

  const messageFieldRefs = useRef({});

  const { update: updateUserCreation, getAll } = useSettings();

  const { update } = useCompanySettings();

  const isSuper = () => {
    return user.super;
  };


  useEffect(() => {

    if (Array.isArray(oldSettings) && oldSettings.length) {

      const userPar = oldSettings.find((s) => s.key === "userCreation");

      if (userPar) {
        setUserCreation(userPar.value);
      }
    }
  }, [oldSettings])


  useEffect(() => {
    if (!settings) return;
    for (const [key, value] of Object.entries(settings)) {
      if (key === "userRating") setUserRating(value);
      if (key === "scheduleType") setScheduleType(value);
      if (key === "chatBotType") setChatBotType(value);
      if (key === "acceptCallWhatsapp") setAcceptCallWhatsapp(value);
      if (key === "userRandom") setUserRandom(value);
      if (key === "sendGreetingMessageOneQueues") setSendGreetingMessageOneQueues(value);
      if (key === "sendSignMessage") setSendSignMessage(value);
      if (key === "sendFarewellWaitingTicket") setSendFarewellWaitingTicket(value);
      if (key === "sendGreetingAccepted") setSendGreetingAccepted(value);
      if (key === "sendQueuePosition") setSendQueuePosition(value);
      if (key === "acceptAudioMessageContact") setAcceptAudioMessageContact(value);
      if (key === "enableLGPD") setEnableLGPD(value);
      if (key === "requiredTag") setRequiredTag(value);
      if (key === "lgpdDeleteMessage") setLGPDDeleteMessage(value)
      if (key === "lgpdHideNumber") setLGPDHideNumber(value);
      if (key === "lgpdConsent") setLGPDConsent(value);
      if (key === "lgpdMessage") setLGPDMessage(value);
      if (key === "sendMsgTransfTicket") setSettingsTransfTicket(value);
      if (key === "lgpdLink") setLGPDLink(value);
      if (key === "DirectTicketsToWallets") setDirectTicketsToWallets(value);
      if (key === "closeTicketOnTransfer") setCloseTicketOnTransfer(value);
      if (key === "transferMessage") setTransferMessage(value);
      if (key === "greetingAcceptedMessage") setGreetingAcceptedMessage(value);
      if (key === "AcceptCallWhatsappMessage") setAcceptCallWhatsappMessage(value);
      if (key === "sendQueuePositionMessage") setSendQueuePositionMessage(value);
      if (key === "showNotificationPending") setShowNotificationPending(value);
      if (key === "autoTranslate") setAutoTranslate(value);
      if (key === "translateApiKey") setTranslateApiKey(value);
      if (key === "aiAutoSummary") setAiAutoSummary(value);
      if (key === "aiAutoSummaryTime") setAiAutoSummaryTime(value);
      if (key === "aiSummaryApiKey") setAiSummaryApiKey(value || "");
      if (key === "sendWelcomeMessage") setSendWelcomeMessage(value);
      if (key === "welcomeMessageTemplate") setWelcomeMessageTemplate(parseWelcomeMessages(value));
      if (key === "flowbuilderAI") setFlowbuilderAI(value);
      if (key === "flowbuilderAIApiKey") setFlowbuilderAIApiKey(value || "");
    }
  }, [settings]);

  async function handleChangeUserCreation(value) {
    setUserCreation(value);
    setLoadingUserCreation(true);
    await updateUserCreation({
      key: "userCreation",
      value,
    });
    setLoadingUserCreation(false);
  }

  async function handleChangeUserRating(value) {
    setUserRating(value);
    setLoadingUserRating(true);
    await update({
      column: "userRating",
      data: value
    });
    setLoadingUserRating(false);
  }

  async function handleScheduleType(value) {
    setScheduleType(value);
    setLoadingScheduleType(true);
    await update({
      column: "scheduleType",
      data: value
    });
    setLoadingScheduleType(false);
    if (typeof scheduleTypeChanged === "function") {
      scheduleTypeChanged(value);
    }
  }

  async function handleChatBotType(value) {
    setChatBotType(value);
    await update({
      column: "chatBotType",
      data: value
    });
    if (typeof scheduleTypeChanged === "function") {
      setChatBotType(value);
    }
  }

  async function handleLGPDMessage(value) {
    setLGPDMessage(value);
    setLoadingLGPDMessage(true);
    await update({
      column: "lgpdMessage",
      data: value
    });
    setLoadingLGPDMessage(false);
  }

  async function handletransferMessage(value) {
    setTransferMessage(value);
    setLoadingTransferMessage(true);
    await update({
      column: "transferMessage",
      data: value
    });
    setLoadingTransferMessage(false);
  }

  async function handleGreetingAcceptedMessage(value) {
    setGreetingAcceptedMessage(value);
    setLoadingGreetingAcceptedMessage(true);
    await update({
      column: "greetingAcceptedMessage",
      data: value
    });
    setLoadingGreetingAcceptedMessage(false);
  }

  async function handleAcceptCallWhatsappMessage(value) {
    setAcceptCallWhatsappMessage(value);
    setLoadingAcceptCallWhatsappMessage(true);
    await update({
      column: "AcceptCallWhatsappMessage",
      data: value
    });
    setLoadingAcceptCallWhatsappMessage(false);
  }

  async function handlesendQueuePositionMessage(value) {
    setSendQueuePositionMessage(value);
    setLoadingSendQueuePositionMessage(true);
    await update({
      column: "sendQueuePositionMessage",
      data: value
    });
    setLoadingSendQueuePositionMessage(false);
  }

  async function handleShowNotificationPending(value) {
    setShowNotificationPending(value);
    setLoadingShowNotificationPending(true);
    await update({
      column: "showNotificationPending",
      data: value
    });
    setLoadingShowNotificationPending(false);
  }

  async function handleAutoTranslate(value) {
    setAutoTranslate(value);
    setLoadingAutoTranslate(true);
    await update({
      column: "autoTranslate",
      data: value
    });
    setLoadingAutoTranslate(false);
  }

  async function handleTranslateApiKey() {
    setLoadingTranslateApiKey(true);
    await update({
      column: "translateApiKey",
      data: translateApiKey
    });
    setLoadingTranslateApiKey(false);
  }

  async function handleSendWelcomeMessage(value) {
    setSendWelcomeMessage(value);
    setLoadingSendWelcomeMessage(true);
    await update({
      column: "sendWelcomeMessage",
      data: value
    });
    setLoadingSendWelcomeMessage(false);
  }

  async function handleWelcomeMessageTemplate() {
    setLoadingWelcomeMessageTemplate(true);
    await update({
      column: "welcomeMessageTemplate",
      data: stringifyWelcomeMessages(welcomeMessageTemplate)
    });
    setLoadingWelcomeMessageTemplate(false);
  }

  async function handleLGPDLink(value) {
    setLGPDLink(value);
    setLoadingLGPDLink(true);
    await update({
      column: "lgpdLink",
      data: value
    });
    setLoadingLGPDLink(false);
  }

  async function handleLGPDDeleteMessage(value) {
    setLGPDDeleteMessage(value);
    setLoadingLGPDDeleteMessage(true);
    await update({
      column: "lgpdDeleteMessage",
      data: value
    });
    setLoadingLGPDDeleteMessage(false);
  }

  async function handleLGPDConsent(value) {
    setLGPDConsent(value);
    setLoadingLGPDConsent(true);
    await update({
      column: "lgpdConsent",
      data: value
    });
    setLoadingLGPDConsent(false);
  }

  async function handleLGPDHideNumber(value) {
    setLGPDHideNumber(value);
    setLoadingLGPDHideNumber(true);
    await update({
      column: "lgpdHideNumber",
      data: value
    });
    setLoadingLGPDHideNumber(false);
  }

  async function handleSendGreetingAccepted(value) {
    setSendGreetingAccepted(value);
    setLoadingSendGreetingAccepted(true);
    await update({
      column: "sendGreetingAccepted",
      data: value
    });
    setLoadingSendGreetingAccepted(false);
  }

  async function handleUserRandom(value) {
    setUserRandom(value);
    setLoadingUserRandom(true);
    await update({
      column: "userRandom",
      data: value
    });
    setLoadingUserRandom(false);
  }

  async function handleSettingsTransfTicket(value) {
    setSettingsTransfTicket(value);
    setLoadingSettingsTransfTicket(true);
    await update({
      column: "sendMsgTransfTicket",
      data: value
    });
    setLoadingSettingsTransfTicket(false);
  }

  async function handleAcceptCallWhatsapp(value) {
    setAcceptCallWhatsapp(value);
    setLoadingAcceptCallWhatsapp(true);
    await update({
      column: "acceptCallWhatsapp",
      data: value
    });
    setLoadingAcceptCallWhatsapp(false);
  }

  async function handleSendSignMessage(value) {
    setSendSignMessage(value);
    setLoadingSendSignMessage(true);
    await update({
      column: "sendSignMessage",
      data: value
    });
    localStorage.setItem("sendSignMessage", value === "enabled" ? true : false); //atualiza localstorage para sessão
    setLoadingSendSignMessage(false);
  }

  async function handleSendGreetingMessageOneQueues(value) {
    setSendGreetingMessageOneQueues(value);
    setLoadingSendGreetingMessageOneQueues(true);
    await update({
      column: "sendGreetingMessageOneQueues",
      data: value
    });
    setLoadingSendGreetingMessageOneQueues(false);
  }

  async function handleSendQueuePosition(value) {
    setSendQueuePosition(value);
    setLoadingSendQueuePosition(true);
    await update({
      column: "sendQueuePosition",
      data: value
    });
    setLoadingSendQueuePosition(false);
  }

  async function handleSendFarewellWaitingTicket(value) {
    setSendFarewellWaitingTicket(value);
    setLoadingSendFarewellWaitingTicket(true);
    await update({
      column: "sendFarewellWaitingTicket",
      data: value
    });
    setLoadingSendFarewellWaitingTicket(false);
  }

  async function handleAcceptAudioMessageContact(value) {
    setAcceptAudioMessageContact(value);
    setLoadingAcceptAudioMessageContact(true);
    await update({
      column: "acceptAudioMessageContact",
      data: value
    });
    setLoadingAcceptAudioMessageContact(false);
  }

  async function handleEnableLGPD(value) {
    setEnableLGPD(value);
    setLoadingEnableLGPD(true);
    await update({
      column: "enableLGPD",
      data: value
    });
    setLoadingEnableLGPD(false);
  }

  async function handleRequiredTag(value) {
    setRequiredTag(value);
    setLoadingRequiredTag(true);
    await update({
      column: "requiredTag",
      data: value,
    });
    setLoadingRequiredTag(false);
  }

  async function handleCloseTicketOnTransfer(value) {
    setCloseTicketOnTransfer(value);
    setLoadingCloseTicketOnTransfer(true);
    await update({
      column: "closeTicketOnTransfer",
      data: value,
    });
    setLoadingCloseTicketOnTransfer(false);
  }

  async function handleDirectTicketsToWallets(value) {
    setDirectTicketsToWallets(value);
    setLoadingDirectTicketsToWallets(true);
    await update({
      column: "DirectTicketsToWallets",
      data: value,
    });
    setLoadingDirectTicketsToWallets(false);
  }

  async function handleFlowbuilderAI(value) {
    setFlowbuilderAI(value);
    setLoadingFlowbuilderAI(true);
    await update({
      column: "flowbuilderAI",
      data: value,
    });
    setLoadingFlowbuilderAI(false);
  }

  async function handleFlowbuilderAIApiKey() {
    setLoadingFlowbuilderAIApiKey(true);
    await update({
      column: "flowbuilderAIApiKey",
      data: flowbuilderAIApiKey,
    });
    setLoadingFlowbuilderAIApiKey(false);
  }

  async function handleAiAutoSummary(value) {
    setAiAutoSummary(value);
    setLoadingAiAutoSummary(true);
    await update({
      column: "aiAutoSummary",
      data: value,
    });
    setLoadingAiAutoSummary(false);
  }

  async function handleAiAutoSummaryTime(value = aiAutoSummaryTime) {
    setLoadingAiAutoSummaryTime(true);
    await update({
      column: "aiAutoSummaryTime",
      data: value,
    });
    setLoadingAiAutoSummaryTime(false);
  }

  async function handleAiSummaryApiKey() {
    setLoadingAiSummaryApiKey(true);
    await update({
      column: "aiSummaryApiKey",
      data: aiSummaryApiKey,
    });
    setLoadingAiSummaryApiKey(false);
  }

  // Constrói o conteúdo do tooltip de ajuda a partir das traduções
  const buildHelpContent = (helpKey) => {
    if (!helpKey) return null;
    const data = i18n.t(`settings.helps.${helpKey}`, {
      returnObjects: true,
      defaultValue: null,
    });
    if (!data || typeof data === "string") return null;
    const description = data.description || "";
    const steps = Array.isArray(data.steps) ? data.steps : [];
    if (!description && steps.length === 0) return null;
    return (
      <div>
        {description && (
          <div className={classes.helpTooltipTitle}>{description}</div>
        )}
        {steps.length > 0 && (
          <ol className={classes.helpTooltipSteps}>
            {steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  // Renderiza ícone de ajuda (HelpCircle) com tooltip rico
  const renderHelpIcon = (helpKey) => {
    const content = buildHelpContent(helpKey);
    if (!content) return null;
    return (
      <AppTooltip title={content} enterTouchDelay={0} leaveTouchDelay={6000}>
        <span className={classes.helpIcon}>
          <HelpCircle size={14} strokeWidth={2} />
        </span>
      </AppTooltip>
    );
  };

  const handleMessageVariableClick = async (fieldName, msgVar, value, handler) => {
    const input = messageFieldRefs.current[fieldName];
    const currentValue = value || "";
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = `${currentValue.substring(0, selectionStart)}${msgVar}${currentValue.substring(selectionEnd)}`;
    const nextCursorPosition = selectionStart + msgVar.length;

    await handler(nextValue);
    setTimeout(() => {
      const nextInput = messageFieldRefs.current[fieldName];
      if (!nextInput) return;
      nextInput.focus();
      nextInput.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }, 100);
  };

  const renderMessageField = ({
    id,
    label,
    value,
    loading,
    onChange,
    minRows = 3,
    helperText = null,
    helpKey = id,
  }) => (
    <FormControl className={classes.selectContainer}>
      <span className={classes.fieldLabel}>
        {label}
        {renderHelpIcon(helpKey)}
      </span>
      <TextField
        id={id}
        name={id}
        margin="dense"
        size="small"
        multiline
        minRows={minRows}
        fullWidth
        variant="outlined"
        value={value}
        required
        inputRef={(el) => {
          messageFieldRefs.current[id] = el;
        }}
        onChange={(e) => onChange(e.target.value)}
      />
      <FormHelperText>
        {loading
          ? i18n.t("settings.settingsConfig.options.updating")
          : helperText}
      </FormHelperText>
      <div className={classes.variablesPickerWrap}>
        <MessageVariablesPicker
          onClick={(msgVar) => handleMessageVariableClick(id, msgVar, value, onChange)}
        />
      </div>
    </FormControl>
  );

  // Helper para renderizar toggle (com conteúdo expandido opcional + ajuda)
  const renderToggle = (label, value, handler, loading, opts = {}, expanded = null, helpKey = null) => {
    const {
      isBoolean,
      onValue = "enabled",
      offValue = "disabled",
      showExpandedWhenOff,
    } = opts;
    const isOn = isBoolean ? !!value : value === onValue;
    const showExpanded = !!expanded && (showExpandedWhenOff ? !isOn : isOn);
    return (
      <div className={classes.toggleCard}>
        <div className={classes.toggleCardRow}>
          <div>
            <span className={classes.toggleLabelWithHelp}>
              <span className={classes.toggleLabel}>{label}</span>
              {renderHelpIcon(helpKey)}
            </span>
            {loading && (
              <div className={classes.toggleHelper}>
                {i18n.t("settings.settingsConfig.options.updating")}
              </div>
            )}
          </div>
          <CustomToggleSwitch
            checked={isOn}
            onChange={(e) => {
              const newVal = isBoolean
                ? e.target.checked
                : e.target.checked ? onValue : offValue;
              handler(newVal);
            }}
          />
        </div>
        {showExpanded && <div className={classes.toggleExpanded}>{expanded}</div>}
      </div>
    );
  };

  return (
    <div className={classes.optionsContainer}>
      <Grid spacing={1} container>

        {/* MOEDA DO SISTEMA — sincronizada com o seletor do card no Kanban */}
        <Grid xs={12} sm={6} md={4} item>
          <div className={classes.toggleItem}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className={classes.toggleLabelWithHelp}>
                <span className={classes.toggleLabel}>
                  {i18n.t("settings.settingsConfig.options.systemCurrency") || "Moeda do sistema"}
                </span>
                {renderHelpIcon("systemCurrency")}
              </span>
              {loadingCurrency && (
                <div className={classes.toggleHelper}>
                  {i18n.t("settings.settingsConfig.options.updating")}
                </div>
              )}
            </div>
            <Select
              value={currency?.code || ""}
              disabled={loadingCurrency}
              disableUnderline
              onChange={async (e) => {
                try {
                  await updateCurrency(e.target.value);
                  toast.success(
                    i18n.t("settings.settingsConfig.options.currencyUpdated")
                    || "Moeda atualizada"
                  );
                } catch (err) {
                  /* toastError já tratado em useCurrency */
                }
              }}
              renderValue={(code) => {
                const cur = (getAvailableCurrencies?.() || []).find((c) => c.code === code);
                if (!cur) return code || "";
                return `${cur.symbol} ${cur.code}`;
              }}
              style={{ fontSize: "0.85rem", fontWeight: 500 }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "right" },
                transformOrigin: { vertical: "top", horizontal: "right" },
                PaperProps: {
                  className: classes.globalMenuPaper,
                  style: { maxHeight: 360 },
                },
              }}
            >
              {(getAvailableCurrencies?.() || []).map((cur) => (
                <MenuItem key={cur.code} value={cur.code}>
                  {`${cur.symbol}  ${cur.code} — ${cur.name}`}
                </MenuItem>
              ))}
            </Select>
          </div>
        </Grid>

        {/* CRIAÇÃO DE COMPANY/USERS */}
        {isSuper() ?
          <Grid xs={12} sm={6} md={4} item>
            {renderToggle(
              i18n.t("settings.settingsConfig.options.creationCompanyUser"),
              userCreation, handleChangeUserCreation, loadingUserCreation,
              {}, null, "userCreation"
            )}
          </Grid>
          : null}

        {/* AVALIAÇÕES */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.evaluations"),
            userRating, handleChangeUserRating, loadingUserRating,
            {}, null, "userRating"
          )}
        </Grid>

        {/* AGENDAMENTO DE EXPEDIENTE - múltiplas opções */}
        <Grid xs={12} sm={6} md={4} item>
          <div className={classes.toggleItem}>
            <div style={{ flex: 1 }}>
              <span className={classes.toggleLabelWithHelp}>
                <span className={classes.toggleLabel}>
                  {i18n.t("settings.settingsConfig.options.officeScheduling")}
                </span>
                {renderHelpIcon("officeScheduling")}
              </span>
              {loadingScheduleType && <div className={classes.toggleHelper}>{i18n.t("settings.settingsConfig.options.updating")}</div>}
            </div>
            <Select
              value={scheduleType}
              onChange={async (e) => { handleScheduleType(e.target.value); }}
              disableUnderline
              style={{ fontSize: '0.85rem', fontWeight: 500 }}
              MenuProps={{
                getContentAnchorEl: null,
                anchorOrigin: { vertical: "bottom", horizontal: "right" },
                transformOrigin: { vertical: "top", horizontal: "right" },
                PaperProps: {
                  className: classes.globalMenuPaper,
                  style: { maxHeight: 360 },
                },
              }}
            >
              <MenuItem value={"disabled"}>{i18n.t("settings.settingsConfig.options.disabled")}</MenuItem>
              <MenuItem value={"queue"}>{i18n.t("settings.settingsConfig.options.queueManagement")}</MenuItem>
              <MenuItem value={"company"}>{i18n.t("settings.settingsConfig.options.companyManagement")}</MenuItem>
              <MenuItem value={"connection"}>{i18n.t("settings.settingsConfig.options.connectionManagement")}</MenuItem>
            </Select>
          </div>
        </Grid>

        {/* ENVIAR SAUDAÇÃO AO ACEITAR O TICKET */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendGreetingAccepted"),
            SendGreetingAccepted, handleSendGreetingAccepted, loadingSendGreetingAccepted,
            {},
            renderMessageField({
              id: "greetingAcceptedMessage",
              label: i18n.t("settings.settingsConfig.customMessages.greetingAcceptedMessage"),
              value: greetingAcceptedMessage,
              loading: loadingGreetingAcceptedMessage,
              onChange: handleGreetingAcceptedMessage,
            }),
            "sendGreetingAccepted"
          )}
        </Grid>

        {/* ESCOLHER OPERADOR ALEATORIO */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.userRandom"),
            UserRandom, handleUserRandom, loadingUserRandom,
            {}, null, "userRandom"
          )}
        </Grid>

        {/* ENVIAR MENSAGEM DE TRANSFERENCIA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendMsgTransfTicket"),
            SettingsTransfTicket, handleSettingsTransfTicket, loadingSettingsTransfTicket,
            {},
            renderMessageField({
              id: "transferMessage",
              label: i18n.t("settings.settingsConfig.customMessages.transferMessage"),
              value: transferMessage,
              loading: loadingTransferMessage,
              onChange: handletransferMessage,
            }),
            "sendMsgTransfTicket"
          )}
        </Grid>

        {/* AVISO SOBRE LIGAÇÃO DO WHATSAPP */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.acceptCallWhatsapp"),
            AcceptCallWhatsapp, handleAcceptCallWhatsapp, loadingAcceptCallWhatsapp,
            { showExpandedWhenOff: true },
            renderMessageField({
              id: "AcceptCallWhatsappMessage",
              label: i18n.t("settings.settingsConfig.customMessages.AcceptCallWhatsappMessage"),
              value: AcceptCallWhatsappMessage,
              loading: loadingAcceptCallWhatsappMessage,
              onChange: handleAcceptCallWhatsappMessage,
            }),
            "acceptCallWhatsapp"
          )}
        </Grid>

        {/* HABILITAR PARA O ATENDENTE RETIRAR O ASSINATURA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendSignMessage"),
            sendSignMessage, handleSendSignMessage, loadingSendSignMessage,
            {}, null, "sendSignMessage"
          )}
        </Grid>

        {/* ENVIAR SAUDAÇÃO QUANDO HOUVER SOMENTE 1 FILA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendGreetingMessageOneQueues"),
            sendGreetingMessageOneQueues, handleSendGreetingMessageOneQueues, loadingSendGreetingMessageOneQueues,
            {}, null, "sendGreetingMessageOneQueues"
          )}
        </Grid>

        {/* ENVIAR MENSAGEM COM A POSIÇÃO DA FILA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendQueuePosition"),
            sendQueuePosition, handleSendQueuePosition, loadingSendQueuePosition,
            {},
            renderMessageField({
              id: "sendQueuePositionMessage",
              label: i18n.t("settings.settingsConfig.customMessages.sendQueuePositionMessage"),
              value: sendQueuePositionMessage,
              loading: loadingSendQueuePositionMessage,
              onChange: handlesendQueuePositionMessage,
            }),
            "sendQueuePosition"
          )}
        </Grid>

        {/* ENVIAR MENSAGEM DE DESPEDIDA NO AGUARDANDO */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendFarewellWaitingTicket"),
            sendFarewellWaitingTicket, handleSendFarewellWaitingTicket, loadingSendFarewellWaitingTicket,
            {}, null, "sendFarewellWaitingTicket"
          )}
        </Grid>

        {/* ACEITAR AUDIO DO CONTATO */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.acceptAudioMessageContact"),
            acceptAudioMessageContact, handleAcceptAudioMessageContact, loadingAcceptAudioMessageContact,
            {}, null, "acceptAudioMessageContact"
          )}
        </Grid>

        {/* LGPD */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.enableLGPD"),
            enableLGPD, handleEnableLGPD, loadingEnableLGPD,
            {}, null, "enableLGPD"
          )}
        </Grid>

        {/* TAG OBRIGATÓRIA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.requiredTag"),
            requiredTag, handleRequiredTag, loadingRequiredTag,
            {}, null, "requiredTag"
          )}
        </Grid>

        {/* FECHAR TICKET AO TRANSFERIR */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.closeTicketOnTransfer"),
            closeTicketOnTransfer, handleCloseTicketOnTransfer, loadingCloseTicketOnTransfer,
            { isBoolean: true }, null, "closeTicketOnTransfer"
          )}
        </Grid>

        {/* NOTIFICAÇÃO PENDENTE */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.showNotificationPending"),
            showNotificationPending, handleShowNotificationPending, loadingShowNotificationPending,
            { isBoolean: true }, null, "showNotificationPending"
          )}
        </Grid>

        {/* TRADUÇÃO AUTOMÁTICA */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            tOption("autoTranslate", "Tradução Automática de Mensagens"),
            autoTranslate, handleAutoTranslate, loadingAutoTranslate,
            {},
            <TextField
              id="translateApiKey"
              label={tOption("translateApiKey", "API Key OpenAI (para tradução)")}
              variant="outlined"
              size="small"
              fullWidth
              value={translateApiKey}
              onChange={(e) => setTranslateApiKey(e.target.value)}
              onBlur={handleTranslateApiKey}
              type="password"
              helperText={tOption("translateApiKeyHelper", "Chave da API OpenAI para tradução automática. Se vazio, usa a chave do primeiro Prompt configurado.")}
            />,
            "autoTranslate"
          )}
        </Grid>

        {/* RESUMO IA DIÁRIO AUTOMÁTICO */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            tOption("aiAutoSummary", "Resumo IA Diário Automático"),
            aiAutoSummary, handleAiAutoSummary, loadingAiAutoSummary,
            {},
            <>
              <TextField
                id="aiSummaryApiKey"
                label={tOption("aiSummaryApiKey", "API Key OpenAI (para resumo IA)")}
                variant="outlined"
                size="small"
                fullWidth
                value={aiSummaryApiKey}
                onChange={(e) => setAiSummaryApiKey(e.target.value)}
                onBlur={handleAiSummaryApiKey}
                type="password"
                helperText={
                  loadingAiSummaryApiKey
                    ? tOption("updating", "Atualizando...")
                    : tOption("aiSummaryApiKeyHelper", "Chave usada somente para resumos IA. Se vazio, usa a chave de tradução ou a chave do primeiro Prompt.")
                }
              />
              <FormControl
                variant="outlined"
                size="small"
                className={classes.summaryTimeSelect}
                disabled={loadingAiAutoSummaryTime}
              >
                <InputLabel id="aiAutoSummaryTime-label">
                  {tOption("aiAutoSummaryTime", "Horário do Resumo IA Diário")}
                </InputLabel>
                <Select
                  id="aiAutoSummaryTime"
                  labelId="aiAutoSummaryTime-label"
                  value={aiAutoSummaryTime || "23:00"}
                  label={tOption("aiAutoSummaryTime", "Horário do Resumo IA Diário")}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAiAutoSummaryTime(value);
                    handleAiAutoSummaryTime(value);
                  }}
                  MenuProps={{
                    getContentAnchorEl: null,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                      className: classes.globalMenuPaper,
                      style: { maxHeight: 320 },
                    },
                  }}
                >
                  {aiSummaryTimeSelectOptions.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {
                  loadingAiAutoSummaryTime
                    ? tOption("updating", "Atualizando...")
                    : tOption("aiAutoSummaryTimeHelper", "Horário em que o resumo automático será gerado para todos os tickets abertos.")
                  }
                </FormHelperText>
              </FormControl>
            </>,
            "aiAutoSummary"
          )}
        </Grid>

        {/* MENSAGEM DE BOAS-VINDAS NO CADASTRO */}
        <Grid xs={12} sm={6} md={4} item>
          {renderToggle(
            i18n.t("settings.settingsConfig.options.sendWelcomeMessage") || "Mensagem de boas-vindas no cadastro",
            sendWelcomeMessage, handleSendWelcomeMessage, loadingSendWelcomeMessage,
            {},
            <div>
              {WELCOME_MESSAGE_LANGUAGES.map((lang) => (
                <div key={lang.code} style={{ marginBottom: 14 }}>
                  {renderMessageField({
                    id: `welcomeMessageTemplate-${lang.code}`,
                    label: `${i18n.t("settings.settingsConfig.options.welcomeMessageTemplate") || "Modelo da mensagem de boas-vindas"} (${lang.label})`,
                    value: welcomeMessageTemplate?.[lang.code] || "",
                    loading: loadingWelcomeMessageTemplate,
                    minRows: 4,
                    helperText: i18n.t("settings.settingsConfig.options.welcomeMessageHelper") || "Use variáveis: {name}, {companyName}, {email}, {phone}, {trialDate}. O sistema envia o texto do idioma escolhido no cadastro.",
                    onChange: async (value) => {
                      const nextMessages = {
                        ...welcomeMessageTemplate,
                        [lang.code]: value
                      };
                      setWelcomeMessageTemplate(nextMessages);
                      setLoadingWelcomeMessageTemplate(true);
                      await update({
                        column: "welcomeMessageTemplate",
                        data: stringifyWelcomeMessages(nextMessages)
                      });
                      setLoadingWelcomeMessageTemplate(false);
                    },
                  })}
                </div>
              ))}
            </div>,
            "sendWelcomeMessage"
          )}
        </Grid>

      </Grid>

      <br></br>
      {/*-----------------LGPD-----------------*/}
      {enableLGPD === "enabled" && (
        <>
          <Grid spacing={3} container
            style={{ marginBottom: 10 }}>
            <Tabs
              value={0}
              indicatorColor="primary"
              textColor="primary"
              scrollButtons="on"
              variant="scrollable"
              className={classes.tab}
            >
              <Tab
                label={i18n.t("settings.settingsConfig.LGPD.title")} />
            </Tabs>
          </Grid>
          <Grid spacing={1} container>
            <Grid xs={12} sm={6} md={12} item>
              {renderMessageField({
                id: "lgpdMessage",
                label: i18n.t("settings.settingsConfig.LGPD.welcome"),
                value: lgpdMessage,
                loading: loadinglgpdMessage,
                onChange: handleLGPDMessage,
              })}
            </Grid>
            <Grid xs={12} sm={6} md={12} item>
              <FormControl className={classes.selectContainer}>
                <TextField
                  id="lgpdLink"
                  name="lgpdLink"
                  margin="dense"
                  label={i18n.t("settings.settingsConfig.LGPD.linkLGPD")}
                  variant="outlined"
                  value={lgpdLink}
                  onChange={async (e) => {
                    handleLGPDLink(e.target.value);
                  }}
                >
                </TextField>
                <FormHelperText>
                  {loadingLGPDLink && i18n.t("settings.settingsConfig.options.updating")}
                </FormHelperText>
              </FormControl>
            </Grid>
            {/* LGPD Manter ou nao mensagem deletada pelo contato */}
            <Grid xs={12} sm={6} md={4} item>
              {renderToggle(
                i18n.t("settings.settingsConfig.LGPD.obfuscateMessageDelete"),
                lgpdDeleteMessage, handleLGPDDeleteMessage, loadingLGPDDeleteMessage,
                {}, null, "lgpdDeleteMessage"
              )}
            </Grid>
            {/* LGPD Sempre solicitar confirmação / consentimento dos dados */}
            <Grid xs={12} sm={6} md={4} item>
              {renderToggle(
                i18n.t("settings.settingsConfig.LGPD.alwaysConsent"),
                lgpdConsent, handleLGPDConsent, loadingLGPDConsent,
                {}, null, "lgpdConsent"
              )}
            </Grid>
            {/* LGPD Ofuscar número telefone para usuários */}
            <Grid xs={12} sm={6} md={4} item>
              {renderToggle(
                i18n.t("settings.settingsConfig.LGPD.obfuscatePhoneUser"),
                lgpdHideNumber, handleLGPDHideNumber, loadingLGPDHideNumber,
                {}, null, "lgpdHideNumber"
              )}
            </Grid>
          </Grid>
        </>
      )}
      <Grid spacing={1} container>

      {/* ====== SAÍDA IA DO FLOWBUILDER ====== */}
      <Grid xs={12} item>
        <Typography variant="subtitle1" style={{ fontWeight: 600, marginTop: 24, marginBottom: 8 }}>
          {tOption("flowbuilderAISectionTitle", "Saída IA do FlowBuilder")}
        </Typography>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          {tOption("flowbuilderAISectionDescription", "Quando ativado, a \"Saída IA\" nos blocos do FlowBuilder usa GPT para ler o histórico da conversa e assumir o atendimento automaticamente quando o tempo expira.")}
        </Typography>
      </Grid>

      <Grid xs={12} sm={6} md={4} item>
        {renderToggle(
          tOption("flowbuilderAI", "Ativar Saída IA no FlowBuilder"),
          flowbuilderAI, handleFlowbuilderAI, loadingFlowbuilderAI,
          {},
          <FormControl className={classes.selectContainer}>
            <TextField
              id="flowbuilderAIApiKey"
              name="flowbuilderAIApiKey"
              margin="dense"
              size="small"
              label={tOption("flowbuilderAIApiKey", "API Key OpenAI (Saída IA)")}
              variant="outlined"
              type="password"
              fullWidth
              placeholder="sk-..."
              value={flowbuilderAIApiKey}
              onChange={(e) => setFlowbuilderAIApiKey(e.target.value)}
              onBlur={handleFlowbuilderAIApiKey}
            />
            <FormHelperText>
              {loadingFlowbuilderAIApiKey
                ? tOption("updating", "Atualizando...")
                : tOption("flowbuilderAIApiKeyHelper", "Chave da API OpenAI usada pela Saída IA dos blocos do FlowBuilder.")}
            </FormHelperText>
          </FormControl>,
          "flowbuilderAI"
        )}
      </Grid>

      </Grid>
    </div>
  );
}
